/**
 * Demo fixtures.
 *
 * DETERMINISTIC AND ENTIRELY FICTIONAL. No `Date.now()`, no randomness, no real
 * company and no real transaction — a demo that names a real acquirer is making
 * a claim about that company, and a demo whose numbers move between screenshots
 * cannot be reviewed.
 *
 * NO NETWORK. Nothing here calls Supabase, a search provider or a model. The
 * transport refuses to invoke in mock mode and these fixtures are what the
 * screens read instead, so a demo cannot spend provider budget by being opened.
 *
 * They deliberately include the UNCOMFORTABLE states, because those are the ones
 * worth showing: a competition analysis that fell back to a table, a Porter
 * force with insufficient evidence, two PESTEL categories with nothing found, a
 * material input conflict, a missing required field, and a bounded
 * counter-evidence search that returned nothing.
 */

import type {
  AnalysisType,
  ClaimView,
  CompetitionBody,
  EvidenceView,
  ExitStrategyBody,
  ExitThesis,
  MarketAssumptions,
  MarketValidationBody,
  PestelBody,
  PorterBody,
  ProvenancedInput,
  ResearchCoverageView,
  ServerReadiness,
  StrategyContextDraft,
} from './types';

/** Fixed, so a screenshot taken today matches one taken next month. */
export const MOCK_GENERATED_AT = '2026-08-10T09:30:00.000Z';
export const MOCK_STARTUP_ID = '00000000-0000-4000-8000-000000000001';
/** Fictional, and long enough to exercise header and node truncation. */
export const MOCK_STARTUP_NAME = 'Demo Reporting AG';

export const MOCK_CONTEXT: Record<string, ProvenancedInput> = {
  offering: {
    value: 'Automated regulatory reporting for independent asset managers',
    originLabel: 'founder',
    verificationLabel: 'founder_provided',
    sourceKeys: ['mock-src-offering'],
    detailAvailable: true,
    founderConfirmed: true,
    founderConfirmedAt: '2026-08-01T10:00:00.000Z',
    conflictDisposition: 'none',
    publicationScope: 'investor_publishable',
  },
  primaryUseCase: {
    value: 'Produce and file quarterly supervisory reports without a manual spreadsheet pass',
    originLabel: 'pitch_deck',
    verificationLabel: 'extracted',
    sourceKeys: ['mock-src-usecase'],
    detailAvailable: true,
    founderConfirmed: true,
    conflictDisposition: 'none',
    publicationScope: 'investor_publishable',
  },
  customerSegments: {
    value: ['Independent asset managers', 'Multi-family offices'],
    originLabel: 'profile',
    verificationLabel: 'founder_provided',
    sourceKeys: ['mock-src-segments'],
    detailAvailable: false,
    founderConfirmed: true,
    conflictDisposition: 'none',
    publicationScope: 'investor_publishable',
  },
  geographies: {
    value: ['Switzerland', 'Liechtenstein'],
    originLabel: 'profile',
    verificationLabel: 'founder_provided',
    sourceKeys: ['mock-src-geo'],
    detailAvailable: false,
    founderConfirmed: true,
    conflictDisposition: 'none',
    publicationScope: 'investor_publishable',
  },
  businessModel: {
    value: 'Annual subscription per entity, tiered by assets under management',
    originLabel: 'founder',
    verificationLabel: 'founder_provided',
    sourceKeys: ['mock-src-model'],
    detailAvailable: true,
    founderConfirmed: true,
    // Resolved by the founder after the deck and the profile disagreed.
    conflictDisposition: 'resolved_by_founder',
    publicationScope: 'investor_publishable',
  },
  differentiators: {
    value: ['Pre-filled from the custodian feed', 'Reviewed by a licensed auditor before filing'],
    originLabel: 'interview',
    verificationLabel: 'founder_provided',
    sourceKeys: ['mock-src-diff'],
    detailAvailable: true,
    founderConfirmed: true,
    conflictDisposition: 'none',
    // Interview-derived, published only because the founder said so.
    publicationScope: 'investor_publishable',
  },
};

/** Setup with a live conflict and a genuinely missing field. */
export const MOCK_CONTEXT_DRAFT: StrategyContextDraft = {
  startupId: MOCK_STARTUP_ID,
  fields: {
    offering: [
      {
        candidateId: 'mock-cand-offering-founder',
        fieldKey: 'offering',
        value: 'Automated regulatory reporting for independent asset managers',
        sourceKind: 'founder',
        independenceGroup: 'founder:1',
        provenancePrecision: 'field_exact',
        assertionStatus: 'asserted',
        verificationState: 'founder_confirmed',
        visibility: 'investor_publishable',
        corroboratingGroups: [],
      },
    ],
    businessModel: [
      {
        candidateId: 'mock-cand-model-deck',
        fieldKey: 'businessModel',
        value: 'Annual subscription per entity, tiered by assets under management',
        sourceKind: 'pitch_deck',
        independenceGroup: 'deck:4',
        provenancePrecision: 'field_exact',
        assertionStatus: 'extracted',
        verificationState: 'unverified',
        visibility: 'investor_publishable',
        extractionConfidence: 0.82,
        corroboratingGroups: [],
        conflictGroup: 'business-model',
      },
      {
        candidateId: 'mock-cand-model-profile',
        fieldKey: 'businessModel',
        value: 'Per-report pricing',
        sourceKind: 'profile',
        independenceGroup: 'profile:1',
        // Legacy profile row: the source is known, the exact field lineage is not.
        provenancePrecision: 'aggregate_only',
        assertionStatus: 'extracted',
        verificationState: 'unverified',
        visibility: 'investor_publishable',
        corroboratingGroups: [],
        conflictGroup: 'business-model',
      },
    ],
    // Present as a key with no candidates: nothing was found, and nothing is
    // substituted for it.
    marketBoundary: [],
  },
  proposed: {
    offering: { candidateId: 'mock-cand-offering-founder', value: 'Automated regulatory reporting for independent asset managers' },
    businessModel: null,
    marketBoundary: null,
  },
  draftFingerprint: 'mock-draft-fingerprint',
  sourceFingerprint: 'mock-source-fingerprint',
  previousApprovedVersion: { id: 'mock-ctx-2', version: 2, sourceFingerprint: 'mock-source-fingerprint-old' },
  sourcesChanged: true,
  readiness: {
    competition: {
      status: 'ready',
      missingRequired: [],
      unconfirmedRequired: [],
      conflicts: [],
      publicationRequired: [],
      optionalGaps: ['knownSubstitutes'],
      questions: [],
    },
    market_validation: {
      status: 'missing_required',
      missingRequired: ['marketBoundary'],
      unconfirmedRequired: [],
      conflicts: [],
      publicationRequired: [],
      optionalGaps: [],
      questions: [
        { fieldKey: 'marketBoundary', question: 'Where does this market start and stop — which offerings are in and out?' },
      ],
    },
  } as Record<string, ServerReadiness>,
};

export const MOCK_COVERAGE: ResearchCoverageView = {
  elements: [
    { methodologyElement: 'candidate_discovery', planned: 3, searched: 3, state: 'supported' },
    { methodologyElement: 'candidate_classification', planned: 2, searched: 2, state: 'supported' },
    { methodologyElement: 'axis_evidence', planned: 4, searched: 4, state: 'conflicted' },
    { methodologyElement: 'buyer_power.buyer_concentration', planned: 2, searched: 0, state: 'budget_exhausted' },
  ],
  totals: { planned: 11, searched: 9, supported: 2, conflicted: 1, retrieved: 0, policyLimited: 0, unknown: 0, skipped: 1 },
  rounds: 2,
  searchCalls: 31,
  independenceGroups: 7,
  primarySourceCoverage: { targeted: 5, achieved: 4 },
  counterEvidenceCoverage: { targeted: 6, completed: 4, noneFound: 2, limited: 1 },
  providerWarnings: [
    {
      providerId: 'mock_search',
      code: 'filter_not_supported',
      message:
        'The domains filter could not be applied by any available provider, so this search was broader than requested.',
      affectedElements: ['axis_evidence'],
    },
  ],
  budgetExhausted: true,
  retrievedAt: MOCK_GENERATED_AT,
};

/** Fell back to the comparison table: the quadrant threshold was not met. */
/**
 * The QUADRANT demo — the primary published shape, and the one the approved
 * design is about. The startup's own coordinates are present because the
 * methodology refuses to publish a map without them.
 */
export const MOCK_COMPETITION: CompetitionBody = {
  outputMode: 'quadrant',
  axes: [
    {
      axis: 'customer_scope',
      lowLabel: 'Smaller / self-serve customers',
      highLabel: 'Larger / enterprise customers',
      relevanceRationale: 'Buyers in this fictional market split sharply by supervised-entity size.',
      evidenceIds: ['mock-ev-2'],
      claimKey: 'competition.axis.customer_scope',
    },
    {
      axis: 'solution_breadth',
      lowLabel: 'Focused point solution',
      highLabel: 'Broad platform',
      relevanceRationale: 'Published feature lists separate single-workflow tools from suites.',
      evidenceIds: ['mock-ev-3'],
      claimKey: 'competition.axis.solution_breadth',
    },
  ],
  self: {
    entityId: 'mock-e-self',
    positions: { customer_scope: 2, solution_breadth: 2 },
    evidenceIds: ['mock-ev-1'],
    founderSupplied: false,
    claimKey: 'competition.self',
  },
  entities: [
    {
      name: 'Fictive Ledger Systems',
      entityId: 'mock-e-1',
      claimKey: 'competition.entity.mock-e-1',
      discovery: 'reconfirmed',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'fictive-ledger.example' },
      classification: 'direct',
      positions: { customer_scope: 4, solution_breadth: 5 },
      evidenceIds: ['mock-ev-2'],
    },
    {
      name: 'Imaginary Compliance Suite',
      entityId: 'mock-e-2',
      claimKey: 'competition.entity.mock-e-2',
      discovery: 'new',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'imaginary-suite.example' },
      classification: 'adjacent',
      positions: { customer_scope: 3, solution_breadth: 4 },
      evidenceIds: ['mock-ev-3'],
    },
    {
      name: 'Spreadsheets and a checklist',
      entityId: 'mock-e-3',
      claimKey: 'competition.entity.mock-e-3',
      discovery: 'reconfirmed',
      identity: { matchMethod: 'normalized_name_jurisdiction', matchConfidence: 'moderate' },
      classification: 'substitute',
      positions: { customer_scope: 1, solution_breadth: 1 },
      evidenceIds: ['mock-ev-4'],
    },
    {
      name: 'Demo Filing Desk',
      entityId: 'mock-e-4',
      claimKey: 'competition.entity.mock-e-4',
      discovery: 'new',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'demo-filing.example' },
      classification: 'direct',
      positions: { customer_scope: 5, solution_breadth: 3 },
      evidenceIds: ['mock-ev-5'],
    },
  ],
  notReFound: [],
  notReassessed: [],
  comparisonStatus: 'available',
  fallbackReason: null,
  insufficientReason: null,
};

/**
 * The FALLBACK demo: the same market, published as a sourced table because no
 * axis pair cleared the dual-axis coverage bar. Kept as a first-class fixture
 * so the uncomfortable output stays reviewable rather than only reachable
 * through a failed run.
 */
export const MOCK_COMPETITION_TABLE_ONLY: CompetitionBody = {
  outputMode: 'comparison_table_only',
  axes: [],
  self: null,
  entities: [
    {
      name: 'Demo Reporting AG',
      entityId: 'mock-e-self',
      claimKey: 'competition.entity.mock-e-self',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'demo-reporting.example' },
      classification: 'direct',
      positions: { customer_scope: 2, solution_breadth: 1 },
      evidenceIds: ['mock-ev-1'],
    },
    {
      name: 'Fictive Ledger Systems',
      entityId: 'mock-e-1',
      claimKey: 'competition.entity.mock-e-1',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'fictive-ledger.example' },
      classification: 'direct',
      positions: { customer_scope: 5 },
      evidenceIds: ['mock-ev-2'],
    },
    {
      name: 'Imaginary Compliance Suite',
      entityId: 'mock-e-2',
      claimKey: 'competition.entity.mock-e-2',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'imaginary-suite.example' },
      classification: 'adjacent',
      positions: { customer_scope: 3, solution_breadth: 4 },
      evidenceIds: ['mock-ev-3'],
    },
    {
      name: 'Spreadsheets and a checklist',
      entityId: 'mock-e-3',
      claimKey: 'competition.entity.mock-e-3',
      identity: { matchMethod: 'normalized_name_jurisdiction', matchConfidence: 'moderate' },
      classification: 'substitute',
      positions: { customer_scope: 1, solution_breadth: 1 },
      evidenceIds: ['mock-ev-4'],
    },
  ],
  notReFound: [],
  notReassessed: [],
  comparisonStatus: 'none',
  fallbackReason:
    'Fewer than 70% of the verified alternatives had evidence on both axes, so the comparison is shown instead of a map.',
  insufficientReason: null,
};

export const MOCK_PORTER: PorterBody = {
  forces: [
    {
      force: 'competitive_rivalry',
      rating: 'moderate',
      drivers: [
        {
          driver: 'competitor_concentration',
          claimKey: 'porter.competitive_rivalry.competitor_concentration',
          pressure: 2,
          observation: 'Three fictional vendors account for most published mandates in the demo market.',
          interpretation: 'A concentrated field raises rivalry for the remaining accounts.',
          evidenceIds: ['mock-ev-2'],
          counterEvidenceStatus: 'completed_with_results',
        },
        {
          driver: 'market_growth',
          claimKey: 'porter.competitive_rivalry.market_growth',
          pressure: 0,
          observation: 'The demo segment grew in each of the last three fictional reporting periods.',
          interpretation: 'Growth absorbs capacity and eases direct rivalry.',
          evidenceIds: ['mock-ev-5'],
          counterEvidenceStatus: 'none_found_after_search',
        },
        {
          driver: 'differentiation',
          claimKey: 'porter.competitive_rivalry.differentiation',
          pressure: 1,
          observation: 'Feature lists overlap; audit review is the stated difference.',
          interpretation: 'Partial differentiation, resting on one claim.',
          evidenceIds: ['mock-ev-6'],
          counterEvidenceStatus: 'completed_with_results',
        },
      ],
      supportedDriverCount: 3,
      unknownDrivers: ['exit_barriers', 'fixed_cost_capacity_pressure', 'customer_switching_costs'],
    },
    {
      force: 'buyer_power',
      // The research budget ran out before this force could be covered.
      rating: 'insufficient_evidence',
      drivers: [],
      supportedDriverCount: 0,
      unknownDrivers: ['buyer_concentration', 'price_sensitivity', 'buyer_switching_costs'],
    },
    {
      force: 'threat_of_new_entrants',
      rating: 'low',
      drivers: [
        {
          driver: 'regulatory_barriers',
          claimKey: 'porter.threat_of_new_entrants.regulatory_barriers',
          pressure: 0,
          observation: 'Filing requires a supervisory registration in the demo jurisdiction.',
          interpretation: 'Registration is a real barrier to a casual entrant.',
          evidenceIds: ['mock-ev-7'],
          counterEvidenceStatus: 'completed_with_results',
        },
        {
          driver: 'capital_requirements',
          claimKey: 'porter.threat_of_new_entrants.capital_requirements',
          pressure: 0,
          observation: 'Fictional peers raised modest seed rounds before first revenue.',
          interpretation: 'Capital is not the binding constraint.',
          evidenceIds: ['mock-ev-8'],
          counterEvidenceStatus: 'none_found_after_search',
        },
        {
          driver: 'reputation_trust_requirements',
          claimKey: 'porter.threat_of_new_entrants.reputation_trust_requirements',
          pressure: 0,
          observation: 'Buyers in the demo market cite auditor sign-off as a purchase condition.',
          interpretation: 'Trust takes time to build and slows new entrants.',
          evidenceIds: ['mock-ev-9'],
          counterEvidenceStatus: 'completed_with_results',
        },
      ],
      supportedDriverCount: 3,
      unknownDrivers: ['scale_or_network_effects'],
    },
    {
      force: 'supplier_power',
      rating: 'moderate',
      drivers: [
        {
          driver: 'platform_data_infrastructure_dependency',
          claimKey: 'porter.supplier_power.platform_data_infrastructure_dependency',
          pressure: 2,
          observation: 'The product depends on one fictional custodian data feed.',
          interpretation: 'A single upstream dependency concentrates supplier leverage.',
          evidenceIds: ['mock-ev-10'],
          counterEvidenceStatus: 'completed_with_results',
        },
        {
          driver: 'alternative_inputs',
          claimKey: 'porter.supplier_power.alternative_inputs',
          pressure: 1,
          observation: 'Two other fictional feeds exist but cover fewer entities.',
          interpretation: 'Alternatives exist and are not equivalent.',
          evidenceIds: ['mock-ev-11'],
          counterEvidenceStatus: 'completed_with_results',
        },
        {
          driver: 'supplier_switching_costs',
          claimKey: 'porter.supplier_power.supplier_switching_costs',
          pressure: 2,
          observation: 'Migrating a feed requires re-mapping every entity.',
          interpretation: 'Switching is expensive, which raises supplier power.',
          evidenceIds: ['mock-ev-12'],
          counterEvidenceStatus: 'none_found_after_search',
        },
      ],
      supportedDriverCount: 3,
      unknownDrivers: ['supplier_concentration', 'forward_integration'],
    },
    {
      force: 'threat_of_substitutes',
      rating: 'high',
      drivers: [
        {
          driver: 'internal_manual_or_no_purchase_alternative',
          claimKey: 'porter.threat_of_substitutes.internal_manual_or_no_purchase_alternative',
          pressure: 2,
          observation: 'Most fictional firms of this size still file using spreadsheets.',
          interpretation: 'Doing nothing is the incumbent, and it is free.',
          evidenceIds: ['mock-ev-4'],
          counterEvidenceStatus: 'completed_with_results',
        },
        {
          driver: 'switching_effort',
          claimKey: 'porter.threat_of_substitutes.switching_effort',
          pressure: 2,
          observation: 'Adopting the product changes the quarterly close process.',
          interpretation: 'Process change is a real cost to the buyer.',
          evidenceIds: ['mock-ev-13'],
          counterEvidenceStatus: 'completed_with_results',
        },
        {
          driver: 'relative_price_performance',
          claimKey: 'porter.threat_of_substitutes.relative_price_performance',
          pressure: 1,
          observation: 'Manual filing costs staff time that the demo firms do not price.',
          interpretation: 'The comparison depends on how a buyer values internal time.',
          evidenceIds: ['mock-ev-14'],
          counterEvidenceStatus: 'none_found_after_search',
        },
      ],
      supportedDriverCount: 3,
      unknownDrivers: ['substitute_availability', 'propensity_to_change_behavior'],
    },
  ],
  overallSynthesis:
    'The binding constraints in this demo are substitutes and supplier dependency; entry barriers work in the venture’s favour. Buyer power is unassessed because the research budget was reached.',
  insufficientReason: null,
};

export const MOCK_PESTEL: PestelBody = {
  categories: [
    {
      category: 'legal',
      posture: 'adverse',
      items: [
        {
          category: 'legal',
          summary: 'A fictional revision to supervisory reporting adds a documented exit plan for critical providers.',
          jurisdiction: 'Demo jurisdiction',
          direction: 'risk',
          materiality: 'high',
          horizon: '0_to_2_years',
          affectedAssumption: 'Enterprise onboarding timelines',
          publishedAt: '2026-07-01T00:00:00.000Z',
          effectiveAt: '2027-01-01T00:00:00.000Z',
          evidenceIds: ['mock-ev-7'],
          claimKey: 'pestel.legal.item.0',
        },
      ],
      claimKey: 'pestel.legal',
    },
    {
      category: 'technological',
      posture: 'favorable',
      items: [
        {
          category: 'technological',
          summary: 'A fictional standard for custodian data exchange reached version 2 in the demo market.',
          jurisdiction: 'Demo jurisdiction',
          direction: 'opportunity',
          materiality: 'moderate',
          horizon: 'current',
          affectedAssumption: 'Integration cost per customer',
          publishedAt: '2026-06-15T00:00:00.000Z',
          evidenceIds: ['mock-ev-10'],
          claimKey: 'pestel.technological.item.0',
        },
      ],
      claimKey: 'pestel.technological',
    },
    {
      category: 'economic',
      posture: 'mixed',
      items: [
        {
          category: 'economic',
          summary: 'Fictional sector technology budgets rose while headcount fell.',
          jurisdiction: 'Demo jurisdiction',
          direction: 'mixed',
          materiality: 'moderate',
          horizon: 'current',
          affectedAssumption: 'Annual value per customer',
          publishedAt: '2026-05-20T00:00:00.000Z',
          evidenceIds: ['mock-ev-15'],
          claimKey: 'pestel.economic.item.0',
        },
      ],
      claimKey: 'pestel.economic',
    },
    // Political, social and environmental are absent on purpose: they render as
    // insufficient evidence, which is what the research actually found.
  ],
  pointsToConsider: [
    {
      kind: 'question',
      text: 'Which of your customer contracts would need a documented exit plan under the revised reporting rules?',
      relatedCategories: ['legal'],
    },
    {
      kind: 'signal_to_monitor',
      text: 'Publication of the final implementing guidance for the revised rules.',
      relatedCategories: ['legal'],
    },
    {
      kind: 'robust_action',
      text: 'Document your provider dependencies now — useful whether or not the rules tighten.',
      relatedCategories: ['legal', 'technological'],
    },
  ],
  insufficientReason: null,
};

export const MOCK_ASSUMPTIONS: MarketAssumptions = {
  currency: 'CHF',
  baseYear: 2026,
  customerUnit: 'licensed asset manager',
  serviceableCustomerUnits: { low: 8_000, base: 10_000, high: 12_000 },
  annualValuePerCustomer: { low: 1_000, base: 1_200, high: 1_500 },
  reachableShare: { low: 0.02, base: 0.03, high: 0.05 },
  founderSam: { low: 11_000_000, base: 13_000_000, high: 15_000_000 },
  founderSourceUrls: [],
};

/**
 * The numbers here are the parity table's supported case, so the demo shows a
 * result the calculation actually produces rather than a hand-written one.
 */
export const MOCK_MARKET: MarketValidationBody = {
  definitionCompatible: true,
  // FIELD BY FIELD, as the report now publishes it. A single boolean derived
  // from "the model wrote no notes" was a fact about the model, not the market.
  definitionComparison: [
    {
      field: 'market_definition',
      founderValue: 'Regulatory reporting for independent asset managers',
      evidenceValue: 'Regulatory reporting software for asset managers',
      compatible: true,
    },
    { field: 'customer_unit', founderValue: 'licensed asset manager', evidenceValue: 'currency', compatible: true, note: 'the published figure is a currency total, so the customer unit does not apply' },
    { field: 'geography', founderValue: 'Demo jurisdiction', evidenceValue: 'Demo jurisdiction', compatible: true },
    { field: 'currency', founderValue: 'CHF', evidenceValue: 'CHF', compatible: true },
    { field: 'base_year', founderValue: '2026', evidenceValue: '2026', compatible: true },
    { field: 'scope', founderValue: 'total and serviceable stated', evidenceValue: 'Published fictional sector revenue', compatible: true },
  ],
  definitionNotes: ['Both figures use CHF, base year 2026, and count licensed managers.'],
  topDown: {
    supported: true,
    base: {
      value: 400_000_000,
      unit: 'currency',
      currency: 'CHF',
      geography: 'Demo jurisdiction',
      baseYear: 2026,
      method: 'reported',
    },
    narrowingFactors: [
      {
        label: 'Regulated segment share',
        low: 0.4,
        base: 0.5,
        high: 0.6,
        evidenceIds: ['mock-ev-15'],
        founderProvided: false,
        claimKey: 'market.top_down.factor.0',
      },
      {
        label: 'Product applicability',
        low: 0.05,
        base: 0.07,
        high: 0.09,
        evidenceIds: ['mock-ev-10'],
        founderProvided: false,
        claimKey: 'market.top_down.factor.1',
      },
    ],
    range: { low: 8_000_000, base: 14_000_000, high: 21_600_000 },
    claimKey: 'market.top_down.base',
  },
  // INDEPENDENT means every input came from retained evidence. The demo shows
  // the inputs so the figure can be checked rather than taken on trust.
  bottomUp: {
    supported: true,
    basis: 'independent',
    sam: { low: 8_000_000, base: 12_000_000, high: 18_000_000 },
    som: { low: 160_000, base: 360_000, high: 900_000 },
    inputs: [
      {
        label: 'Licensed managers in the demo register',
        low: 8_000,
        base: 10_000,
        high: 12_000,
        evidenceIds: ['mock-ev-15'],
        founderProvided: false,
        claimKey: 'market.bottom_up.input.0',
      },
      {
        label: 'Published annual licence spend per manager',
        low: 1_000,
        base: 1_200,
        high: 1_500,
        evidenceIds: ['mock-ev-10'],
        founderProvided: false,
        claimKey: 'market.bottom_up.input.1',
      },
      {
        label: 'Share won by a challenger in the first three years',
        low: 0.02,
        base: 0.03,
        high: 0.05,
        evidenceIds: ['mock-ev-2'],
        founderProvided: false,
        claimKey: 'market.bottom_up.input.2',
      },
    ],
    claimKey: 'market.bottom_up',
  },
  // The founder's own figures, in their own field. Never an input above.
  founderEstimate: {
    tam: { low: 40_000_000, base: 60_000_000, high: 90_000_000 },
    sam: { low: 11_000_000, base: 13_000_000, high: 15_000_000 },
  },
  corroboratedRange: { low: 10_000_000, base: 14_000_000, high: 18_000_000 },
  methodConflict: false,
  conclusion: 'supported',
  conclusionExplanation:
    'The top-down and independent bottom-up estimates overlap. The corroborated range is their intersection.',
  comparisonClaimKey: 'market.comparison',
  insufficientReason: null,
};

export const MOCK_EXIT_THESIS: ExitThesis = {
  id: 'mock-thesis-3',
  version: 3,
  createdAt: '2026-06-01T00:00:00.000Z',
  preferredPathways: ['strategic_acquisition'],
  openToAssessment: false,
  horizonEarliestYear: 2029,
  horizonLatestYear: 2031,
  targetValuationLow: 40_000_000,
  targetValuationHigh: 60_000_000,
  targetValuationCurrency: 'CHF',
  buyerCharacteristics: ['Fictional core-banking vendor with a compliance line'],
  rationale: 'Distribution into supervised firms is the scarce asset, and an incumbent buys distribution.',
  assumptions: ['The custodian feed stays available on current terms.'],
};

export const MOCK_EXIT: ExitStrategyBody = {
  pathways: [
    {
      pathway: 'strategic_acquisition',
      tier: 'supported',
      drivers: [
        { driver: 'buyer_adjacency', judgement: 'supports', rationale: 'Adjacent fictional product lines.', evidenceIds: ['mock-ev-16'], claimKey: 'exit.pathway.strategic_acquisition.driver.buyer_adjacency', ground: 'evidence_backed' },
        { driver: 'sector_acquisition_activity', judgement: 'supports', rationale: 'Several disclosed fictional deals in the demo market.', evidenceIds: ['mock-ev-17'], claimKey: 'exit.pathway.strategic_acquisition.driver.sector_acquisition_activity', ground: 'evidence_backed' },
        { driver: 'integration_value', judgement: 'supports', rationale: 'Shared supervisory rails.', evidenceIds: ['mock-ev-18'], claimKey: 'exit.pathway.strategic_acquisition.driver.integration_value', ground: 'evidence_backed' },
        // Unevidenced, and LABELLED so rather than dropped. "We could not
        // establish this" is information an investor wants.
        { driver: 'buyer_capacity', judgement: 'unknown', rationale: 'No public balance-sheet evidence was found.', evidenceIds: [], claimKey: 'exit.pathway.strategic_acquisition.driver.buyer_capacity', ground: 'ai_consideration' },
      ],
      evidenceIds: ['mock-ev-16', 'mock-ev-17', 'mock-ev-18'],
      claimKey: 'exit.pathway.strategic_acquisition',
    },
    {
      pathway: 'continued_independence',
      tier: 'plausible_with_caveats',
      drivers: [
        { driver: 'durable_economics', judgement: 'supports', rationale: 'Gross margin holds across the fictional cohort.', evidenceIds: ['mock-ev-19'], claimKey: 'exit.pathway.continued_independence.driver.durable_economics', ground: 'evidence_backed' },
        { driver: 'financing_and_cash_generation_viability', judgement: 'mixed', rationale: 'Runway is short at the current burn.', evidenceIds: ['mock-ev-20'], claimKey: 'exit.pathway.continued_independence.driver.financing_and_cash_generation_viability', ground: 'evidence_backed' },
        { driver: 'strategic_optionality', judgement: 'supports', rationale: 'No exclusivity in the fictional feed contract.', evidenceIds: ['mock-ev-21'], claimKey: 'exit.pathway.continued_independence.driver.strategic_optionality', ground: 'evidence_backed' },
      ],
      evidenceIds: ['mock-ev-19', 'mock-ev-21'],
      claimKey: 'exit.pathway.continued_independence',
    },
  ],
  mostSupportedPathways: ['strategic_acquisition'],
  candidates: [
    {
      name: 'Fictive Ledger Systems',
      entityId: 'mock-e-1',
      fitTier: 'plausible_candidate',
      dimensions: [
        { dimension: 'product_use_case_adjacency', supported: true, rationale: 'Overlapping fictional compliance modules.', evidenceIds: ['mock-ev-16'], ground: 'evidence_backed' },
        { dimension: 'customer_channel_adjacency', supported: true, rationale: 'Sells to the same fictional buyer role.', evidenceIds: ['mock-ev-17'], ground: 'evidence_backed' },
        { dimension: 'acquisition_partnership_pattern', supported: false, rationale: 'No disclosed acquisitions in this fictional segment.', evidenceIds: [], ground: 'ai_consideration' },
        { dimension: 'capacity_integration_feasibility', supported: false, rationale: 'No public capacity evidence was found.', evidenceIds: [], ground: 'ai_consideration' },
      ],
      evidenceIds: ['mock-ev-16', 'mock-ev-17'],
      counterargument: {
        text: 'Its published fictional roadmap points at payments rather than supervisory reporting.',
        ground: 'evidence_backed',
        claimKey: 'exit.candidate.mock-e-1.counterargument',
        evidenceIds: ['mock-ev-18'],
      },
      evidenceGaps: ['No public statement about compliance tooling strategy.'],
      claimKey: 'exit.candidate.mock-e-1',
      identity: { matchMethod: 'domain', matchConfidence: 'high', domain: 'fictive-ledger.example' },
    },
  ],
  comparables: [
    {
      buyer: 'Imaginary Core Group',
      target: 'Demo Regtech AG',
      announcedAt: '2025-11-04T00:00:00.000Z',
      rationale: 'Fictional compliance module consolidation.',
      // Undisclosed stays undisclosed, in the demo too.
      valueDisclosed: false,
      evidenceIds: ['mock-ev-22'],
      claimKey: 'exit.comparable.0',
    },
  ],
  // SPECULATION IS LABELLED. These are produced from the surrounding material
  // and nothing external verifies them; they used to render as plain strings
  // beside evidenced findings and read exactly like them.
  readinessGaps: [{ text: 'No audited financial statements yet.', ground: 'ai_consideration' }],
  milestones: [
    { text: 'Complete a first external audit.', ground: 'ai_consideration' },
    { text: 'Publish a documented data-exit procedure.', ground: 'ai_consideration' },
  ],
  thesisComparison: 'partially_aligned',
  assessedThesisVersionId: 'mock-thesis-3',
  insufficientReason: null,
};

export const MOCK_SNAPSHOT = {
  strategyContextVersion: 3,
  strategyContextVersionId: 'mock-ctx-3',
  marketAssumptionVersion: 2,
  exitThesisVersion: 3,
  methodologyVersion: '1.0.0',
  generatedAt: MOCK_GENERATED_AT,
  context: MOCK_CONTEXT,
};

export const MOCK_DERIVATION = {
  methodologyVersion: '1.0.0',
  controlledElements: ['candidate_discovery', 'candidate_classification', 'axis_evidence'],
  thresholds: [
    { label: 'Dual-axis evidence coverage', value: '70% of verified alternatives' },
    { label: 'Minimum verified alternatives for a map', value: '3' },
  ],
  transformations: ['Axis pair rejected: coverage 50% is below the 70% threshold.'],
  fallbackReason:
    'Fewer than 70% of the verified alternatives had evidence on both axes, so the comparison is shown instead of a map.',
  freshness: 'Political and legal evidence expires after 90 days; economic and technological after 180.',
  explanation:
    'Alternatives were discovered, verified and classified against the fixed axis library. No axis pair met the coverage threshold, so the comparison table was published instead of a positioning map.',
};

// ---------------------------------------------------------------------------
// Retained sources and the claims that cite them
// ---------------------------------------------------------------------------
//
// Every source is FICTIONAL — a demo that cites a real publisher is making a
// claim about that publisher. `canDisplay: false` on one of them is
// deliberate: a licence that permits corroboration but not republication is a
// real case, and the modal has to show attribution without the excerpt.

export const MOCK_SOURCES: EvidenceView[] = [
  {
    evidenceId: 'mock-ev-1',
    title: 'Demo supervisory reporting market note',
    publisher: 'Fictive Sector Institute',
    canonicalUrl: 'https://example.invalid/demo-market-note',
    publishedAt: '2026-05-14T00:00:00.000Z',
    retrievedAt: MOCK_GENERATED_AT,
    excerpt: 'Independent asset managers file quarterly supervisory returns under the revised regime.',
    sourceQuality: 'reputable_secondary',
    independenceGroup: 'fictive-sector-institute',
    canDisplay: true,
  },
  {
    evidenceId: 'mock-ev-2',
    title: 'Imaginary Register of licensed managers',
    publisher: 'Demo Federal Registry',
    canonicalUrl: 'https://example.invalid/demo-register',
    publishedAt: '2026-03-02T00:00:00.000Z',
    retrievedAt: MOCK_GENERATED_AT,
    excerpt: 'The register lists licensed asset managers by canton.',
    sourceQuality: 'primary_official',
    independenceGroup: 'demo-federal-registry',
    canDisplay: true,
  },
  {
    evidenceId: 'mock-ev-3',
    title: 'Fictive Ledger Systems product documentation',
    publisher: 'Fictive Ledger Systems',
    canonicalUrl: 'https://example.invalid/fictive-ledger-docs',
    retrievedAt: MOCK_GENERATED_AT,
    excerpt: 'Modules cover onboarding, reconciliation and regulatory filing for enterprise clients.',
    sourceQuality: 'company_official',
    independenceGroup: 'fictive-ledger-systems',
    canDisplay: true,
  },
  {
    // Licensed for corroboration, not for republication.
    evidenceId: 'mock-ev-4',
    title: 'Demo subscription research note',
    publisher: 'Imaginary Analysts',
    publishedAt: '2026-06-20T00:00:00.000Z',
    retrievedAt: MOCK_GENERATED_AT,
    sourceQuality: 'reputable_secondary',
    independenceGroup: 'imaginary-analysts',
    canDisplay: false,
  },
];

/**
 * One citable claim per analysis, so the demo can open the evidence modal.
 *
 * The statements match what each body looks for — the competition claim key,
 * the porter methodology element, the PESTEL item summary — because the bodies
 * find their claim by those, not by position.
 */
export const MOCK_CLAIMS: Record<AnalysisType, ClaimView[]> = {
  competition: [
    {
      claimKey: 'competition.entity.mock-e-1',
      kind: 'estimate',
      statement: 'Fictive Ledger Systems is an evidence-supported direct alternative.',
      supportState: 'supported',
      material: true,
      methodologyElement: 'candidate_classification',
      evidenceIds: ['mock-ev-3', 'mock-ev-1'],
      links: [{ evidenceId: 'mock-ev-3', role: 'supports' as const }, { evidenceId: 'mock-ev-1', role: 'supports' as const }],
      counterEvidenceStatus: 'completed_with_results',
    },
  ],
  porter: [
    {
      claimKey: 'porter.competitive_rivalry.competitor_concentration',
      kind: 'fact',
      statement: 'Four fictional vendors hold most published mandates in this demo segment.',
      supportState: 'supported',
      material: true,
      methodologyElement: 'competitive_rivalry.competitor_concentration',
      evidenceIds: ['mock-ev-1', 'mock-ev-4'],
      links: [{ evidenceId: 'mock-ev-1', role: 'supports' as const }, { evidenceId: 'mock-ev-4', role: 'supports' as const }],
      counterEvidenceStatus: 'none_found_after_search',
    },
  ],
  pestel: [
    {
      claimKey: 'pestel.legal.0',
      kind: 'fact',
      statement: 'Revised outsourcing rules require documented exit plans for critical providers.',
      supportState: 'supported',
      material: true,
      methodologyElement: 'legal',
      evidenceIds: ['mock-ev-2'],
      links: [{ evidenceId: 'mock-ev-2', role: 'supports' as const }],
      counterEvidenceStatus: 'completed_with_results',
    },
  ],
  market_validation: [
    {
      claimKey: 'market.top_down.base',
      kind: 'calculation',
      statement: 'The published base market was narrowed to the regulated segment before any comparison.',
      supportState: 'supported',
      material: true,
      methodologyElement: 'top_down_base',
      evidenceIds: ['mock-ev-1', 'mock-ev-2'],
      links: [{ evidenceId: 'mock-ev-1', role: 'supports' as const }, { evidenceId: 'mock-ev-2', role: 'supports' as const }],
      counterEvidenceStatus: 'completed_with_results',
    },
  ],
  exit_strategy: [
    {
      claimKey: 'exit.candidate.mock-e-1',
      kind: 'inference',
      statement: 'Fictive Ledger Systems overlaps this venture on product and customer adjacency.',
      supportState: 'partially_supported',
      material: true,
      methodologyElement: 'acquirer_fit',
      evidenceIds: ['mock-ev-3'],
      links: [{ evidenceId: 'mock-ev-3', role: 'supports' as const }],
      counterEvidenceStatus: 'budget_exhausted',
    },
  ],
};
